import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Order } from 'protocol/api/billing_new/dto_order_new_pb';

import { useAppSelector } from '@/shared/hooks';

import { billingModel } from '@/entities/billing';

import { billingHistoryMessages, orderStatusText } from '../config/i18n';
import {
  getOrderDiagnocoinAdjustment,
  getOrderListPrice,
  getOrderRemovalInfo,
  getOrderRemovedSubscriptionName,
  getOrderStatus,
  getOrderSubscriptionName,
  getReplacementCauseMessage,
  OrderStatus,
  toDateFromUnixLikeValue,
} from '../lib';

const addEffectCases = new Set(['SubscriptionAdd', 'PackageAdd']);

const getProductStateIDFromAdd = (order: Order): string | undefined => {
  if (!addEffectCases.has(order.Effect?.Effect.case ?? '')) {
    return undefined;
  }

  if (!order.Processed?.IsProcessed) {
    return undefined;
  }

  return order.Processed.CreatedProductStateID || undefined;
};

export type BillingHistoryOrderData = {
  id: string;
  key: string;
  date: string;
  createdAt: string;
  createdBy?: string;
  transactionNumber: string;
  subject: string;
  status: OrderStatus;
  statusText: string;
  markedAsPaid: boolean;
  isDeleted: boolean;
  canMarkAsPaid: boolean;
  canDelete: boolean;
  amount?: string;
  dueDate?: string;
  invoiceID?: string;
  description?: string;
  processedAt?: string;
  processedBy?: string;
  deletedBy?: string;
  deletedAt?: string;
  invoiceURL?: string;
  removalReason?: string;
  removalComment?: string;
  coinsSubtracted?: string;
};

// TODO: Remove Number constructor when in protocol fix bigint
export const useBillingHistory = (): BillingHistoryOrderData[] => {
  const { formatDate, formatMessage, formatNumber, formatTime } = useIntl();
  const orders = useAppSelector(billingModel.selectors.selectOrders);
  const products = useAppSelector(billingModel.selectors.selectInventory);

  return useMemo(() => {
    const formatDateTime = (date?: Date) => {
      if (!date) {
        return undefined;
      }

      return `${formatDate(date, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      })} ${formatTime(date, {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    };

    const formatDateOnly = (date?: Date) =>
      date
        ? formatDate(date, {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
          })
        : '';

    const removeOrderByProductStateID = new Map<string, Order>();
    const productStatesWithProcessedAdd = new Set<string>();

    orders.forEach((order) => {
      const removalInfo = getOrderRemovalInfo(order);
      if (removalInfo) {
        removeOrderByProductStateID.set(removalInfo.productStateID, order);
      }

      const addProductStateID = getProductStateIDFromAdd(order);
      if (addProductStateID) {
        productStatesWithProcessedAdd.add(addProductStateID);
      }
    });

    const productStatesToMerge = new Set<string>();
    productStatesWithProcessedAdd.forEach((productStateID) => {
      if (removeOrderByProductStateID.has(productStateID)) {
        productStatesToMerge.add(productStateID);
      }
    });

    return orders
      .map((order): BillingHistoryOrderData | null => {
        if (
          order.Kind.case === 'Service' &&
          order.Effect?.Effect.case === 'SubscriptionSetAutoRenewal'
        ) {
          return null;
        }

        const removalInfo = getOrderRemovalInfo(order);
        if (
          removalInfo &&
          productStatesToMerge.has(removalInfo.productStateID)
        ) {
          return null;
        }

        const billed =
          order.Kind.case === 'Billed' ? order.Kind.value : undefined;
        const coinAdjustment = getOrderDiagnocoinAdjustment(order);
        const isDeleted = Boolean(order.Deleted?.IsDeleted);
        const createdAtDate = order.Created?.At?.toDate();
        const status = getOrderStatus({
          isPaid: Boolean(billed?.Paid),
          isOverdue: Boolean(billed?.Overdue),
          isDeleted,
          isProcessedWithoutBilling:
            Boolean(coinAdjustment) && Boolean(order.Processed?.IsProcessed),
        });
        const subscriptionAddEffect =
          order.Effect?.Effect.case === 'SubscriptionAdd'
            ? order.Effect.Effect.value
            : undefined;
        const replacementCause = getReplacementCauseMessage(order);
        const scheduledStartAt = subscriptionAddEffect?.StartAt;
        const scheduledSubject = scheduledStartAt
          ? formatMessage(
              {
                id: 'billingHistory.subject.scheduledFor',
                defaultMessage: 'Scheduled for {date}',
              },
              {
                date: `${formatDate(scheduledStartAt.toDate(), {
                  year: 'numeric',
                  month: 'long',
                  day: '2-digit',
                })}`,
              },
            )
          : null;
        const subscriptionName =
          getOrderSubscriptionName(order, products) ??
          formatMessage({
            id: 'billingHistory.unknownProduct',
            defaultMessage: 'Unknown',
          });

        const getBaseSubject = () => {
          if (coinAdjustment) {
            return formatMessage(
              billingHistoryMessages.diagnocoinAdjustmentSubject,
              { amount: formatNumber(coinAdjustment.amount) },
            );
          }

          if (removalInfo) {
            return formatMessage(
              {
                id: 'billingHistory.subject.removal',
                defaultMessage: 'Removal: {name}',
              },
              { name: getOrderRemovedSubscriptionName(order, products) },
            );
          }

          if (replacementCause) {
            return formatMessage(
              {
                id: 'billingHistory.subject.replacementActivation',
                defaultMessage: 'Activation: {name} ({cause})',
              },
              {
                name: subscriptionName,
                cause: formatMessage(replacementCause),
              },
            );
          }

          return subscriptionName;
        };

        const baseSubject = getBaseSubject();
        const removalReasonDescriptor =
          removalInfo?.reason ?? coinAdjustment?.reason;
        const removalReason = removalReasonDescriptor
          ? formatMessage(removalReasonDescriptor)
          : undefined;
        const createdAt = formatDateTime(createdAtDate) || '';
        const dueDate =
          billed && billed.DueDate > 0
            ? formatDate(toDateFromUnixLikeValue(billed.DueDate), {
                year: 'numeric',
                month: 'long',
                day: '2-digit',
              })
            : undefined;
        const formatAmount = (value: string, currency: string) =>
          formatNumber(Number(value), {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
        const listPrice = getOrderListPrice(order);
        const getAmount = () => {
          if (!billed?.Amount || !billed.Currency) {
            return undefined;
          }

          const invoiced = formatAmount(billed.Amount, billed.Currency);
          if (!billed.CustomPrice || !listPrice) {
            return invoiced;
          }

          return formatMessage(billingHistoryMessages.customPriceAmount, {
            amount: invoiced,
            listPrice: formatAmount(listPrice, billed.Currency),
          });
        };
        const amount = getAmount();
        const processedAt =
          order.Processed?.IsProcessed && order.Processed.At
            ? formatDateTime(order.Processed.At.toDate())
            : undefined;
        const deletedAt =
          order.Deleted?.IsDeleted && order.Deleted.At
            ? formatDateTime(order.Deleted.At.toDate())
            : undefined;

        const addProductStateID = getProductStateIDFromAdd(order);
        const mergedRemoveOrder =
          addProductStateID && productStatesToMerge.has(addProductStateID)
            ? removeOrderByProductStateID.get(addProductStateID)
            : undefined;
        const mergedRemovalInfo = getOrderRemovalInfo(mergedRemoveOrder);
        const mergedRemovalReason = mergedRemovalInfo?.reason
          ? formatMessage(mergedRemovalInfo.reason)
          : undefined;
        const mergedRemovalCreatedAtDate =
          mergedRemoveOrder?.Created?.At?.toDate();
        const mergedRemovalProcessedAtDate =
          mergedRemoveOrder?.Processed?.IsProcessed &&
          mergedRemoveOrder.Processed.At
            ? mergedRemoveOrder.Processed.At.toDate()
            : undefined;
        const mergedDeletedAt =
          mergedRemoveOrder &&
          formatDateTime(
            mergedRemovalProcessedAtDate ?? mergedRemovalCreatedAtDate,
          );
        const mergedDeletedBy = mergedRemoveOrder?.Processed?.IsProcessed
          ? mergedRemoveOrder.Processed.By || undefined
          : mergedRemoveOrder?.Created?.By || undefined;

        const ownDeletedBy =
          order.Deleted?.IsDeleted && order.Deleted.By
            ? order.Deleted.By
            : undefined;

        const effectiveStatus: OrderStatus = mergedRemoveOrder
          ? 'deleted'
          : status;

        return {
          id: order.ID,
          key: order.ID,
          date: mergedRemoveOrder
            ? formatDateOnly(mergedRemovalCreatedAtDate)
            : formatDateOnly(createdAtDate),
          createdAt,
          createdBy: order.Created?.By || undefined,
          subject: scheduledSubject
            ? `${baseSubject} (${scheduledSubject})`
            : baseSubject,
          transactionNumber: billed?.TransactionNumber || '',
          status: effectiveStatus,
          statusText: formatMessage(orderStatusText[effectiveStatus]),
          markedAsPaid: Boolean(billed?.MarkedAsPaid),
          isDeleted: isDeleted || Boolean(mergedRemoveOrder),
          canMarkAsPaid: Boolean(
            billed && effectiveStatus === 'new' && !billed.InvoiceID,
          ),
          canDelete:
            !mergedRemoveOrder &&
            Boolean(
              billed &&
                !billed.Paid &&
                !billed.MarkedAsPaid &&
                !billed.Cancelled &&
                !isDeleted,
            ),
          amount,
          dueDate,
          invoiceID: billed?.InvoiceID || undefined,
          description: order.Description || undefined,
          processedAt,
          processedBy:
            order.Processed?.IsProcessed && order.Processed.By
              ? order.Processed.By
              : undefined,
          deletedBy: mergedRemoveOrder ? mergedDeletedBy : ownDeletedBy,
          deletedAt: mergedRemoveOrder ? mergedDeletedAt : deletedAt,
          invoiceURL: billed?.InvoiceURL || undefined,
          removalReason: mergedRemoveOrder
            ? mergedRemovalReason
            : removalReason,
          removalComment: mergedRemoveOrder
            ? mergedRemovalInfo?.comment
            : removalInfo?.comment ?? coinAdjustment?.comment,
          coinsSubtracted: coinAdjustment
            ? formatNumber(coinAdjustment.amount)
            : undefined,
        };
      })
      .filter((row): row is BillingHistoryOrderData => row !== null)
      .reverse();
  }, [orders, products, formatDate, formatMessage, formatNumber, formatTime]);
};
